import React, { useEffect, useState } from "react";
import {
  Box,
  Slider,
  Typography,
  ButtonGroup,
  Button,
  Select,
  MenuItem,
  Divider,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SpeedIcon from "@mui/icons-material/Speed";
import DeleteIcon from "@mui/icons-material/Delete";
import * as math from "mathjs";

const particleTypes = {
  Kugeln: {
    classic: true,
    w: { unit: "cm", min: 2, max: 25 },
    d: { unit: "cm", min: 1, max: 50 },
    e: { unit: "cm", value: 35 },
    defaultScale: 1,
  },
  Photonen: {
    classic: false,
    lambda: { unit: "nm", min: 1, max: 1200 },
    energy: { unit: "eV", min: 1, max: 500 },
    w: { unit: "um", min: 100, max: 1000 },
    d: { unit: "um", min: 100, max: 2000 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 1,
  },
  Elektronen: {
    classic: false,
    lambda: { unit: "pm", min: 1, max: 40 },
    energy: { unit: "keV", min: 1, max: 500 },
    w: { unit: "nm", min: 100, max: 1000 },
    d: { unit: "nm", min: 100, max: 2000 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 3,
  },
  Myonen: {
    classic: false,
    lambda: { unit: "pm", min: 100, max: 2500 },
    energy: { unit: "meV", min: 1, max: 500 },
    w: { unit: "um", min: 1, max: 100 },
    d: { unit: "um", min: 1, max: 200 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 2,
  },
  Protonen: {
    classic: false,
    lambda: { unit: "nm", min: 1, max: 30 },
    energy: { unit: "ueV", min: 1, max: 500 },
    w: { unit: "um", min: 1, max: 100 },
    d: { unit: "um", min: 1, max: 200 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 1,
  },
  Neutronen: {
    classic: false,
    lambda: { unit: "nm", min: 1, max: 30 },
    energy: { unit: "ueV", min: 1, max: 500 },
    w: { unit: "um", min: 1, max: 100 },
    d: { unit: "um", min: 1, max: 200 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 1,
  },
  "He-Atome": {
    classic: false,
    lambda: { unit: "pm", min: 20, max: 450 },
    energy: { unit: "meV", min: 1, max: 500 },
    w: { unit: "um", min: 1, max: 50 },
    d: { unit: "um", min: 1, max: 100 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 3,
  },
  "Na-Moleküle": {
    classic: false,
    lambda: { unit: "pm", min: 5, max: 130 },
    energy: { unit: "meV", min: 1, max: 500 },
    w: { unit: "um", min: 1, max: 100 },
    d: { unit: "um", min: 1, max: 200 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 3,
  },
  "Cs-Atome": {
    classic: false,
    lambda: { unit: "pm", min: 4, max: 80 },
    energy: { unit: "meV", min: 1, max: 500 },
    w: { unit: "um", min: 1, max: 100 },
    d: { unit: "um", min: 1, max: 200 },
    e: { unit: "m", value: 3.5 },
    defaultScale: 3,
  },
};

const ControlPanel = ({
  w,
  setW,
  d,
  setD,
  setPercentageD,
  setPercentageW,
  lambda,
  setLambda,
  scale,
  setScale,
  handleFire,
  isDoubleSlit,
  setIsDoubleSlit,
  maximumReached,
  handleFastFire,
  isFiring,
  classic,
  setClassic,
  handleClearParticles,
  showBarChart,
  setShowBarChart,
  showCurve,
  setShowCurve,
  setCameraPosition1,
  setCameraPosition2,
  shotParticleCount,
}) => {
  const [particleType, setParticleType] = useState("Photonen");
  const [energy, setEnergy] = useState(math.unit(1, "eV"));
  const [particleCount, setParticleCount] = useState(100);

  useEffect(() => {
    setD(
      math.unit(
        particleTypes[particleType].d.min,
        math.unit(particleTypes[particleType].d.unit)
      )
    );
    setW(
      math.unit(
        particleTypes[particleType].w.min,
        math.unit(particleTypes[particleType].w.unit)
      )
    );
    if (!particleTypes[particleType].classic) {
      setClassic(false);
      setEnergy(
        math.unit(
          particleTypes[particleType].energy.min,
          math.unit(particleTypes[particleType].energy.unit)
        )
      );
      calculateWavelength(
        math
          .unit(
            particleTypes[particleType].energy.min,
            math.unit(particleTypes[particleType].energy.unit)
          )
          .toNumber(particleTypes[particleType].energy.unit)
      );
    } else {
      setClassic(true);
    }

    setScale(scaleValues[particleTypes[particleType].defaultScale]);
  }, [particleType]);

  useEffect(() => {
    const percentageD =
      ((d.toNumber(particleTypes[particleType].d.unit) -
        particleTypes[particleType].d.min) /
        (particleTypes[particleType].d.max -
          particleTypes[particleType].d.min)) *
      100;
    setPercentageD(percentageD);
  }, [particleType, d]);

  useEffect(() => {
    const percentageW =
      ((w.toNumber(particleTypes[particleType].w.unit) -
        particleTypes[particleType].w.min) /
        (particleTypes[particleType].w.max -
          particleTypes[particleType].w.min)) *
      100;
    setPercentageW(percentageW);
  }, [particleType, w]);

  function calculateWavelength(energy) {
    console.log(energy, particleTypes[particleType].energy.unit);
    let energyUnit = math.unit(energy, particleTypes[particleType].energy.unit);
    setEnergy(energyUnit);

    const h = math.unit("6.626e-34", "m^2 kg / s"); // Planck's constant in Js
    const c = math.unit("3.0e8", "m / s"); // Speed of light in m/s

    let mass = math.unit("0", "kg");
    let wavelength = math.unit(1, "m");

    switch (particleType) {
      case "Photonen":
        wavelength = math.divide(math.multiply(h, c), energyUnit);
        setLambda(wavelength);
        break;
      case "Protonen":
        mass = math.unit(1.673e-27, "kg");
        break;
      case "Elektronen":
        mass = math.unit(9.109e-31, "kg");
        break;
      case "Neutronen":
        mass = math.unit(1.675e-27, "kg");
        break;
      case "Myonen":
        mass = math.unit(1.884e-28, "kg");
        break;
      case "He-Atome":
        mass = math.unit(6.646e-27, "kg");
        break;
      case "Na-Moleküle":
        mass = math.unit(3.817e-26, "kg");
        break;
      case "Cs-Atome":
        mass = math.unit(2.206e-25, "kg");
        break;
      default:
        throw new Error("Unbekannter Teilchentyp");
    }
    if (mass.toNumber("kg")) {
      console.log(mass);
      wavelength = math.divide(
        h,
        math.sqrt(math.multiply(2, mass, energyUnit))
      );
      setLambda(wavelength);
    }
    
  }

  const scaleMarks = [
    {
      value: 0,
      label: (
        <span>
          &times;1<sup></sup>
        </span>
      ),
    },
    {
      value: 1,
      label: (
        <span>
          &times;10<sup></sup>
        </span>
      ),
    },
    {
      value: 2,
      label: (
        <span>
          &times;10<sup>2</sup>
        </span>
      ),
    },
    {
      value: 3,
      label: (
        <span>
          &times;10<sup>3</sup>
        </span>
      ),
    },
    {
      value: 4,
      label: (
        <span>
          &times;10<sup>4</sup>
        </span>
      ),
    },
    {
      value: 5,
      label: (
        <span>
          &times;10<sup>5</sup>
        </span>
      ),
    },
  ];

  const scaleValues = [1, 10, 100, 1000, 10000, 100000];

  const handleSlitChange = (event, newSlit) => {
    if (newSlit !== null) {
      setIsDoubleSlit(newSlit);
    }
  };

  return (
    <Box
      sx={{
        width: 300,
        minWidth: 300,
        padding: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
      className={"control-menu"}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
        Präparation
      </Typography>
      <Box sx={{ marginLeft: 2, marginRight: 2, marginBottom: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ textAlign: "center", marginBottom: 0 }}
        >
          Teilchentyp
        </Typography>
        <Select
          fullWidth
          value={particleType}
          onChange={(e) => setParticleType(e.target.value)}
          sx={{
            backgroundColor: "white",
            padding: "4px 8px", // Weniger Padding für geringere Höhe
            height: "36px", // Anpassung der Höhe
            "& .MuiSelect-select": {
              padding: "6px 8px", // Anpassung des Innenabstands des Select-Textes
            },
          }}
        >
          {Object.keys(particleTypes).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ marginLeft: 2, marginRight: 2 }}>
        {!particleTypes[particleType].classic && (
          <>
            <Typography
              variant="subtitle1"
              sx={{ textAlign: "center", marginBottom: -2, marginTop: -2 }}
            >
              Energie:{" "}
              {energy
                .toNumber(particleTypes[particleType].energy.unit)
                .toFixed(0)}
              {"\u2009"}
              {particleTypes[particleType].energy.unit}
            </Typography>
            <Slider
              sx={{ marginBottom: 3 }}
              value={energy
                .toNumber(particleTypes[particleType].energy.unit)
                .toFixed(0)}
              onChange={(e, newValue) => calculateWavelength(newValue)}
              min={particleTypes[particleType].energy.min}
              max={particleTypes[particleType].energy.max}
              marks={[
                {
                  value: particleTypes[particleType].energy.min,
                  label: particleTypes[particleType].energy.min,
                },
                {
                  value: particleTypes[particleType].energy.max,
                  label: particleTypes[particleType].energy.max,
                },
              ]}
              valueLabelDisplay={"off"}
            />
          </>
        )}
        <Box sx={{ marginBottom: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: "center", marginTop: -2 }}
          >
            Spalte
          </Typography>
          <ToggleButtonGroup
            value={isDoubleSlit}
            exclusive
            onChange={handleSlitChange}
            aria-label="Spaltauswahl"
            fullWidth
          >
            <ToggleButton color="primary" value={1} aria-label="Linker Spalt">
              Linker
            </ToggleButton>
            <ToggleButton color="primary" value={2} aria-label="Beide Spalte">
              Beide
            </ToggleButton>
            <ToggleButton color="primary" value={0} aria-label="Rechter Spalt">
              Rechter
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ textAlign: "center", marginBottom: -2 }}
        >
          Spaltbreite:{" "}
          {w.toNumber(particleTypes[particleType].w.unit).toFixed(0)}
          {"\u2009"}
          {particleTypes[particleType].w.unit}
        </Typography>
        <Slider
          sx={{ marginBottom: 0 }}
          value={w.toNumber(particleTypes[particleType].w.unit).toFixed(0)}
          onChange={(e, newValue) =>
            setW(math.unit(newValue, particleTypes[particleType].w.unit))
          }
          min={particleTypes[particleType].w.min}
          max={particleTypes[particleType].w.max}
          marks={[
            {
              value: particleTypes[particleType].w.min,
              label: particleTypes[particleType].w.min,
            },
            {
              value: particleTypes[particleType].w.max,
              label: particleTypes[particleType].w.max,
            },
          ]}
          valueLabelDisplay={"off"}
        />
        <Typography
          variant="subtitle1"
          sx={{ textAlign: "center", marginBottom: -2 }}
        >
          Spaltabstand:{" "}
          {d.toNumber(particleTypes[particleType].d.unit).toFixed(0)}
          {"\u2009"}
          {particleTypes[particleType].d.unit}
        </Typography>
        <Slider
          sx={{ marginBottom: 1 }}
          value={d.toNumber(particleTypes[particleType].d.unit)}
          onChange={(e, newValue) =>
            setD(math.unit(newValue, particleTypes[particleType].d.unit))
          }
          min={particleTypes[particleType].d.min}
          max={particleTypes[particleType].d.max}
          marks={[
            {
              value: particleTypes[particleType].d.min,
              label: particleTypes[particleType].d.min,
            },
            {
              value: particleTypes[particleType].d.max,
              label: particleTypes[particleType].d.max,
            },
          ]}
          valueLabelDisplay={"off"}
        />
      </Box>
      <Divider />
      <Box sx={{ marginLeft: 2, marginRight: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ textAlign: "center", marginBottom: -2 }}
        >
          Schirmskalierung
        </Typography>
        <Slider
          value={scaleValues.indexOf(scale)}
          onChange={(e, newValue) => setScale(scaleValues[newValue])}
          step={1}
          marks={scaleMarks}
          min={0}
          max={5}
        />
      </Box>
      <Divider />
      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
        Durchführung
      </Typography>
      {/* <Typography gutterBottom>Distance to Screen</Typography>
      <Slider
        value={distance}
        onChange={(e, value) => setDistance(value)}
        step={0.1}
        min={1}
        max={5}
        valueLabelDisplay="auto"
      /> */}
      <ButtonGroup
        orientation="vertical"
        variant="contained"
        aria-label="contained button group"
      >
        <Box sx={{ display: "flex", alignItems: "stretch" }}>
          <TextField
            type="number"
            size="small"
            value={particleCount}
            onChange={(e) => {
              const value = e.target.value;

              // Verhindert die Eingabe von nicht-numerischen Zeichen
              if (/^\d{0,}$/.test(value)) {
                const numericValue = parseInt(value, 10);

                // Begrenzung auf maximal 9999
                if (numericValue <= 9999) {
                  setParticleCount(value);
                } else {
                  setParticleCount(1); // Setzt den Wert auf 1, falls die Grenze überschritten wird
                }
              }
            }}
            sx={{
              width: 80,
              marginRight: 0,
            }}
            inputProps={{
              max: 9999, // Maximale Eingabe
            }}
          />
          <Button
            color="primary"
            startIcon={<RocketLaunchIcon />}
            disabled={maximumReached}
            onClick={() => {
              handleFire(parseInt(particleCount));
            }}
            sx={{ flexGrow: 1 }} // Damit der Button den restlichen Platz einnimmt
          >
            Teilchen abfeuern
          </Button>
        </Box>
        <Button
          color={isFiring ? "error" : "primary"}
          startIcon={<SpeedIcon />}
          disabled={maximumReached}
          onClick={handleFastFire}
        >
          {isFiring ? "Stoppe Abfeuern" : "Schnelles Abfeuern"}
        </Button>
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleClearParticles}
        >
          {shotParticleCount.length} Teilchen Löschen
        </Button>
      </ButtonGroup>
      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
        Auswertung
      </Typography>
      {/* <FormControlLabel
          control={
            <Checkbox
              checked={showBarChart}
              onChange={() => setShowBarChart(!showBarChart)}
            />
          }
          label="Balkendiagramm"
        /> */}
      <Box sx={{ marginBottom: 2 }}>
        <ToggleButtonGroup
          value={showCurve ? "curve" : ""}
          exclusive
          onChange={() => setShowCurve(!showCurve)}
          aria-label="Theoretische Intensität"
          fullWidth
        >
          <ToggleButton
            color="primary"
            value="curve"
            aria-label="Theoretische Intensität"
          >
            Theoretische Intensität
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
        Kamera
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <ButtonGroup variant="outlined" fullWidth>
          <Button onClick={setCameraPosition1}>AUFBAU</Button>
          <Button onClick={setCameraPosition2}>SCHIRM</Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
};

export default ControlPanel;
